import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@istari-frontend/ui";
import { Eye, Globe, Pencil, X } from "lucide-react";

import { ConnectionShare, SharedConnection } from "~/secure-connections/model/connection-share";
import { EditAccess, ShareableSendingConnection } from "~/secure-connections/model/secure-connection";

export type OutOfTenantShare = SharedConnection & { editAccess: EditAccess };

/**
 * Shares over connections missing from `shareableConnections` (scoped to the caller's tenant)
 * belong to another tenant: the caller can revoke them but cannot change their level. Keyed by
 * connection id. Empty until both lists have loaded, so nothing flashes as out-of-tenant.
 */
export function selectOutOfTenantShares(
  shares: ReadonlyArray<ConnectionShare> | undefined,
  shareableConnections: ReadonlyArray<{ id: string }> | undefined,
): Map<string, OutOfTenantShare> {
  const byConnectionId = new Map<string, OutOfTenantShare>();
  if (!shares || !shareableConnections) return byConnectionId;
  const shareableIds = new Set(shareableConnections.map((c) => c.id));
  for (const share of shares) {
    if (shareableIds.has(share.sendingConnectionId) || byConnectionId.has(share.sendingConnectionId)) continue;
    byConnectionId.set(share.sendingConnectionId, {
      id: share.sendingConnectionId,
      label: share.sendingConnectionLabel,
      editAccess: share.editAccess,
    });
  }
  return byConnectionId;
}

const OUT_OF_TENANT_SHARE_HINT = "Shared over a connection outside your tenant";

// Level is fixed (re-sharing over another tenant's connection is not offered); removal is allowed.
export function OutOfTenantShareRow({
  share,
  onRemove,
  disabled,
}: {
  share: OutOfTenantShare;
  onRemove: () => void;
  disabled?: boolean;
}) {
  return (
    <div
      className="grid grid-cols-[1fr_auto_auto] items-center gap-2 border-b-[0.5px] px-2.5 py-2 last:border-b-0"
      title={OUT_OF_TENANT_SHARE_HINT}
      data-testid={`out-of-tenant-share-${share.id}`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <Avatar className="size-7">
          <AvatarFallback className="size-7 text-xs">
            {connectionInitials(share.label) || <Globe className="size-3.5" />}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col">
          <span className="text-foreground truncate text-sm">{share.label}</span>
          <span className="text-muted-foreground truncate text-xs leading-tight font-light">
            {OUT_OF_TENANT_SHARE_HINT}
          </span>
        </div>
      </div>
      <Badge variant="subtle" size="xs">
        {share.editAccess === "editor" ? <Pencil className="size-3" /> : <Eye className="size-3" />}
        {share.editAccess === "editor" ? "Edit access" : "View access"}
      </Badge>
      <RemoveShareButton label={share.label} onClick={onRemove} disabled={disabled} />
    </div>
  );
}

export function ShareableSendingConnectionRow({
  connection,
  shareLevel,
  onLevelChange,
  onRemove,
  disabled,
}: {
  connection: ShareableSendingConnection;
  shareLevel: EditAccess;
  onLevelChange: (level: EditAccess) => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const initials = connectionInitials(connection.label);

  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2 border-b-[0.5px] px-2.5 py-2 last:border-b-0">
      <div className="flex min-w-0 items-center gap-2">
        <Avatar className="size-7">
          <AvatarFallback className="size-7 text-xs">{initials || <Globe className="size-3.5" />}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col">
          <span className="text-foreground truncate text-sm" title={connection.label}>
            {connection.label}
          </span>
          {connection.description && (
            <span className="text-muted-foreground truncate text-xs leading-tight font-light">
              {connection.description}
            </span>
          )}
        </div>
      </div>
      <ShareLevelControl
        connectionEditAccess={connection.editAccess}
        shareLevel={shareLevel}
        onChange={onLevelChange}
        disabled={disabled}
        connectionLabel={connection.label}
      />
      <RemoveShareButton label={connection.label} onClick={onRemove} disabled={disabled} />
    </div>
  );
}

function ShareLevelControl({
  connectionEditAccess,
  shareLevel,
  onChange,
  disabled,
  connectionLabel,
}: {
  connectionEditAccess: EditAccess;
  shareLevel: EditAccess;
  onChange: (level: EditAccess) => void;
  disabled?: boolean;
  connectionLabel: string;
}) {
  // The connection's ceiling is the sender's per-connection setting. When it's
  // "viewer" the SDK rejects Editor for files on this connection, so lock the
  // control to View — no selector, just an informational badge.
  if (connectionEditAccess === "viewer") {
    return (
      <Badge variant="subtle" size="xs" title="This connection only permits view access.">
        <Eye className="size-3" />
        View access
      </Badge>
    );
  }

  return (
    <Select value={shareLevel} onValueChange={(val) => onChange(val as EditAccess)} disabled={disabled}>
      <SelectTrigger
        variant="ghost"
        size="sm"
        className="h-6 w-fit text-xs"
        aria-label={`Share level for ${connectionLabel}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="viewer" className="text-xs">
          <Eye className="size-3" />
          View access
        </SelectItem>
        <SelectItem value="editor" className="text-xs">
          <Pencil className="size-3" />
          Edit access
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

function RemoveShareButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={`Remove ${label}`}
      className="[&_svg]:text-muted-foreground hover:[&_svg]:text-foreground focus:[&_svg]:text-foreground h-6 w-6 [&_svg]:opacity-50 hover:[&_svg]:opacity-100 focus:[&_svg]:opacity-100"
      onClick={onClick}
      disabled={disabled}
    >
      <X className="size-4" />
    </Button>
  );
}

function connectionInitials(label: string): string {
  return label
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] ?? "")
    .join("")
    .toUpperCase();
}
